import { useEffect } from 'react';

const EASING = 0.09;
const SETTLE = 0.02;
const FINE_POINTER = '(hover: hover) and (pointer: fine)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

const useMouseGlow = () => {
  useEffect(() => {
    const root = document.documentElement;
    const setGlow = (x, y) => {
      root.style.setProperty('--mouse-x', `${x}%`);
      root.style.setProperty('--mouse-y', `${y}%`);
    };

    // Set initial position to center
    setGlow(50, 50);

    // A coarse pointer never produces a cursor position worth following.
    if (!window.matchMedia(FINE_POINTER).matches) return undefined;

    const toPercent = event => ({
      x: (event.clientX / window.innerWidth) * 100,
      y: (event.clientY / window.innerHeight) * 100,
    });

    if (window.matchMedia(REDUCED_MOTION).matches) {
      const handleMove = event => {
        const { x, y } = toPercent(event);
        setGlow(x, y);
      };

      window.addEventListener('mousemove', handleMove, { passive: true });
      return () => window.removeEventListener('mousemove', handleMove);
    }

    let target = { x: 50, y: 50 };
    let current = { x: 50, y: 50 };
    let rafId = null;

    const tick = () => {
      current.x += (target.x - current.x) * EASING;
      current.y += (target.y - current.y) * EASING;

      // Park the loop whenever the glow has caught up, so an idle page costs nothing.
      if (Math.abs(target.x - current.x) < SETTLE && Math.abs(target.y - current.y) < SETTLE) {
        current = { ...target };
        setGlow(current.x, current.y);
        rafId = null;
        return;
      }

      setGlow(current.x, current.y);
      rafId = window.requestAnimationFrame(tick);
    };

    const handleMouseMove = event => {
      target = toPercent(event);
      if (rafId === null) rafId = window.requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, []);
};

export default useMouseGlow;
